import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GiKnifeFork, GiCupcake, GiPartyPopper, GiHotMeal, GiCakeSlice, GiDiamondRing, GiSpotedFlower, GiPartyFlags } from 'react-icons/gi';
import { FiShoppingBag, FiMapPin, FiCalendar, FiArrowRight } from 'react-icons/fi';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase/client';
import SEO from '../components/SEO';
import HeroSlider from '../components/HeroSlider';
import ServicesSlider from '../components/ServicesSlider';
import AboutSection from '../components/AboutSection';
import CouponBanner from '../components/CouponBanner';

export default function Home() {
  const [featuredBranches, setFeaturedBranches] = useState([]);
  const [popularAddOns, setPopularAddOns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchHomeData = async () => {
      try {
        setLoading(true);

        // Fetch featured branches
        const branchesCollection = collection(db, 'branches');
        const branchesQuery = query(branchesCollection, where('isActive', '==', true), limit(3));
        const branchesSnapshot = await getDocs(branchesQuery);
        const branchesList = branchesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Fetch popular add-ons
        const addOnsCollection = collection(db, 'addOns');
        const addOnsQuery = query(addOnsCollection, where('isAvailable', '==', true), limit(4));
        const addOnsSnapshot = await getDocs(addOnsQuery);
        const addOnsList = addOnsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setFeaturedBranches(branchesList);
        setPopularAddOns(addOnsList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching home data:', error);
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      }
    },
    hover: {
      y: -10,
      transition: {
        duration: 0.2,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <>
      <CouponBanner />
      <SEO
        title="Home"
        description="Welcome to Happy Bubble - Your premier destination for booking party halls and event spaces in Bangalore. Find the perfect venue for your celebrations."
        keywords={[
          'party hall booking bangalore',
          'event space booking',
          'celebration venues',
          'party halls in bangalore',
          'event booking bangalore',
          'Happy Bubble',
          'party venues',
          'event spaces'
        ]}
        url="/"
      />
      <div className="flex flex-col min-h-screen bg-gray-50">
        <div className="flex-grow">
          {/* Replace the old Hero Section with the new HeroSlider */}
          <HeroSlider />

          <AboutSection />

          {/* Floating icons animation */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-5 gap-16 -mt-0">
              {[
                { icon: <GiPartyPopper />, delay: 0, color: 'var(--primary)', label: 'Party' },
                { icon: <GiCakeSlice />, delay: 0.1, color: 'var(--secondary)', label: 'Cake' },
                { icon: <GiDiamondRing />, delay: 0.2, color: 'var(--accent)', label: 'Ring' },
                { icon: <GiSpotedFlower />, delay: 0.3, color: 'var(--primary)', label: 'Decor' },
                { icon: <GiPartyFlags />, delay: 0.4, color: 'var(--secondary)', label: 'Celebrate' }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="flex flex-col items-center"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{
                    y: [0, -10, 0],
                    opacity: 1
                  }}
                  transition={{
                    y: {
                      repeat: Infinity,
                      duration: 2,
                      delay: item.delay,
                      repeatType: 'reverse'
                    },
                    opacity: { duration: 0.5, delay: item.delay }
                  }}
                >
                  <div
                    className="h-16 w-16 rounded-full flex items-center justify-center text-2xl transform hover:scale-110 transition-transform duration-300 bg-white/95"
                    style={{
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                      color: item.color,
                      backdropFilter: 'blur(8px)'
                    }}
                  >
                    {item.icon}
                  </div>

                </motion.div>
              ))}
            </div>
          </div>

          {/* Popular Add-ons Section */}
          <div className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="lg:text-center mb-10">
                <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: 'var(--primary)' }}>Customize Your Experience</h2>
                <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight sm:text-4xl font-heading" style={{ color: 'var(--text-primary)' }}>
                  Popular Add-ons
                </p>
                <p className="mt-4 max-w-2xl text-xl mx-auto lg:mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  Enhance your booking with these popular options
                </p>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
                </div>
              ) : popularAddOns.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-lg text-gray-500">No add-ons available at the moment</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {popularAddOns.map((addon) => (
                    <motion.div
                      key={addon.id}
                      variants={cardVariants}
                      initial="hidden"
                      whileInView="visible"
                      whileHover="hover"
                      viewport={{ once: true, amount: 0.3 }}
                      className="bg-white p-6 rounded-lg shadow-md"
                    >
                      <div className="h-40 mb-4 relative rounded-md overflow-hidden">
                        {addon.imageUrl ? (
                          <img
                            src={addon.imageUrl}
                            alt={addon.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-amber-100 flex items-center justify-center">
                            <GiPartyPopper className="text-4xl text-amber-600" />
                          </div>
                        )}
                      </div>
                      <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>{addon.name}</h3>
                      <p className="text-sm mt-1 text-gray-500 line-clamp-2">{addon.description}</p>
                      <p className="mt-2 font-medium text-lg" style={{ color: 'var(--primary)' }}>₹{addon.price.toLocaleString()}</p>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>


          {/* Featured Branches Section */}
          <div className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="lg:text-center mb-10">
                <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: 'var(--primary)' }}>Explore Our Theatres</h2>
                <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight sm:text-4xl font-heading" style={{ color: 'var(--text-primary)' }}>
                  Featured Theatres
                </p>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
                </div>
              ) : featuredBranches.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-lg text-gray-500">No theatres available at the moment</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {featuredBranches.map((branch) => (
                      <motion.div
                        key={branch.id}
                        variants={cardVariants}
                        initial="hidden"
                        whileInView="visible"
                        whileHover="hover"
                        viewport={{ once: true, amount: 0.3 }}
                        className="card bg-white overflow-hidden shadow-md rounded-lg"
                      >
                        <div className="h-48 relative">
                          {branch.imageUrl ? (
                            <img
                              src={branch.imageUrl}
                              alt={branch.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                              <FiMapPin className="text-4xl text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <h3 className="text-xl font-bold font-heading" style={{ color: 'var(--primary)' }}>{branch.name}</h3>
                          <p className="mt-2 text-sm text-gray-500">{branch.address}</p>
                          <div className="mt-4 flex flex-wrap gap-2">
                            {branch.features && branch.features.slice(0, 3).map((feature, index) => (
                              <span
                                key={index}
                                className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800"
                              >
                                {feature}
                              </span>
                            ))}
                          </div>
                          <div className="mt-4">
                            <Link
                              to={`/branches/${branch.id}`}
                              className="flex items-center text-sm font-medium"
                              style={{ color: 'var(--primary)' }}
                            >
                              Explore <FiArrowRight className="ml-1" />
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-10 text-center">
                    <Link
                      to="/branches"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium"
                      style={{
                        backgroundColor: 'var(--primary)',
                        color: 'white',
                        boxShadow: 'var(--btn-shadow)'
                      }}
                    >
                      <span>View All Theatres</span>
                      <FiArrowRight />
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* About Section */}
          {/* <div className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="lg:text-center">
                <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: 'var(--primary)' }}>Our Concept</h2>
                <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight sm:text-4xl font-heading" style={{ color: 'var(--text-primary)' }}>
                  Dining with a Theatrical Twist
                </p>
                <p className="mt-4 max-w-2xl text-xl lg:mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  At TheatreEats, we transform ordinary dining into extraordinary experiences with immersive themes and private spaces.
                </p>
              </div>

              <div className="mt-16">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <motion.div 
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    whileHover="hover"
                    viewport={{ once: true, amount: 0.3 }}
                    className="card"
                  >
                    <img 
                      src="https://images.unsplash.com/photo-1520209268518-aec60b8bb5ca?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1180&q=80" 
                      alt="Private dining space" 
                      className="w-full h-64 object-cover rounded-t-lg"
                      style={{ borderRadius: 'var(--radius-md) var(--radius-md) 0 0' }}
                    />
                    <div className="p-6">
                      <h3 className="text-xl font-bold font-heading" style={{ color: 'var(--primary)' }}>Private Theatre Rooms</h3>
                      <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>Exclusive spaces for your celebrations with customizable themes</p>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    whileHover="hover"
                    viewport={{ once: true, amount: 0.3 }}
                    className="card"
                  >
                    <img 
                      src="https://images.unsplash.com/photo-1555244162-803834f70033?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1740&q=80" 
                      alt="Gourmet dining" 
                      className="w-full h-64 object-cover rounded-t-lg"
                      style={{ borderRadius: 'var(--radius-md) var(--radius-md) 0 0' }}
                    />
                    <div className="p-6">
                      <h3 className="text-xl font-bold font-heading" style={{ color: 'var(--primary)' }}>Gourmet Experiences</h3>
                      <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>Delicious cuisine with theatrical presentation</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>

          <div className="divider-wave"></div> */}

          {/* Features Section */}
          <div className="py-16 food-pattern-bg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="lg:text-center">
                <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: 'var(--secondary)' }}>Features</h2>
                <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight sm:text-4xl font-heading" style={{ color: 'var(--text-primary)' }}>
                  Perfect Blend of Dining & Entertainment
                </p>
                <p className="mt-4 max-w-2xl text-xl lg:mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  Our theatrical restaurants offer unique experiences for every occasion.
                </p>
              </div>

              <div className="mt-16">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    {
                      icon: <FiMapPin />,
                      title: "Multiple Locations",
                      description: "Choose from our various branches, each with unique themes and convenient locations."
                    },
                    {
                      icon: <GiCupcake />,
                      title: "Birthday Packages",
                      description: "Special themed packages for birthdays with custom decorations and personalized menus."
                    },
                    {
                      icon: <FiCalendar />,
                      title: "Outdoor Decorations",
                      description: "We offer a wide range of outdoor decorations to make your event unforgettable."
                    },
                    {
                      icon: <GiPartyPopper />,
                      title: "Photo Sessions",
                      description: "Instagrammable spaces with professional lighting for perfect photoshoots."
                    },
                    {
                      icon: <GiHotMeal />,
                      title: "Themed Experiences",
                      description: "Choose from various themes like vintage cinema, underwater world, or futuristic space."
                    },
                    {
                      icon: <FiShoppingBag />,
                      title: "Easy Reservations",
                      description: "Book online with real-time availability and instant confirmation."
                    },
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      className="pt-6"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="card h-full">
                        <div>
                          <div className="-mt-6 mb-4">
                            <span
                              className="inline-flex items-center justify-center p-3 rounded-full shadow-lg"
                              style={{
                                background: `linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)`,
                                color: 'white',
                                fontSize: '1.5rem'
                              }}
                            >
                              {feature.icon}
                            </span>
                          </div>
                          <h3 className="text-lg font-medium tracking-tight font-heading mb-2" style={{ color: 'var(--text-primary)' }}>
                            {feature.title}
                          </h3>
                          <p style={{ color: 'var(--text-secondary)' }}>
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="divider-dots"></div>

          {/* Testimonials Section */}
          <div className="py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-center text-3xl font-extrabold font-heading sm:text-4xl" style={{ color: 'var(--text-primary)' }}>
                What Our Guests Say
              </h2>
              <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {[
                  {
                    initial: "S",
                    name: "Sarah J",
                    location: "Event Planner",
                    comment: "Happy Bubble transformed our corporate event into something extraordinary! The interactive theater experience left our guests talking for days. It wasn’t just a show; it was an experience."
                  },
                  {
                    initial: "S",
                    name: "Shree",
                    location: "Private Party Organizer",
                    comment: "I was looking for something unique for my birthday party, and Happy Bubble delivered in every way. The surprise elements kept everyone on their toes, and the immersive experience made the night unforgettable!"
                  },
                  {
                    initial: "V",
                    name: "Veena",
                    location: "Corporate Event Coordinator",
                    comment: "Happy Bubble was the perfect partner for our gallery event. They seamlessly integrated their theater into the art exhibit, creating an immersive environment that transported our guests into another world. Truly innovative!"
                  }
                ].map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    className="card-funky overflow-hidden h-full"
                  >
                    <div className="px-6 py-8">
                      <div className="flex items-center">
                        <div
                          className="flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center"
                          style={{
                            background: `linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)`,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1.25rem'
                          }}
                        >
                          <span>{testimonial.initial}</span>
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-medium font-heading" style={{ color: 'var(--text-primary)' }}>{testimonial.name}</h3>
                          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{testimonial.location}</p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="flex mb-2">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className="text-lg" style={{ color: 'var(--secondary)' }}>★</span>
                          ))}
                        </div>
                        <p className="italic" style={{ color: 'var(--text-secondary)' }}>"{testimonial.comment}"</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Newsletter Section */}
          <section className="py-16 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-2" style={{ background: `linear-gradient(90deg, var(--primary), var(--secondary), var(--accent))` }}></div>
            <div
              className="absolute inset-0 z-0"
              style={{ backgroundColor: 'var(--primary)', opacity: 0.05 }}
            ></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-extrabold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>
                  Stay Updated
                </h2>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Subscribe to our newsletter for special offers and new theme announcements
                </p>
              </div>

              <div className="max-w-md mx-auto">
                <form className="flex">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-grow px-4 py-3 rounded-l-full"
                    style={{
                      borderColor: 'var(--primary)',
                      borderWidth: '2px',
                      borderRightWidth: '0',
                      backgroundColor: 'white'
                    }}
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-5 py-3 rounded-r-full"
                    style={{
                      backgroundColor: 'var(--primary)',
                      color: 'white',
                      boxShadow: 'var(--btn-shadow)'
                    }}
                  >
                    Subscribe
                  </motion.button>
                </form>
              </div>

              <div className="flex justify-center mt-8">
                <div className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                  We respect your privacy and will never share your information
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-12 relative confetti overflow-hidden">
            <div
              className="absolute inset-0 z-0"
              style={{
                background: `linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)`,
                opacity: 0.9
              }}
            ></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="text-center py-12">
                <motion.h2
                  className="text-4xl font-bold font-display mb-4 text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  Ready to Plan Your Event?
                </motion.h2>
                <motion.p
                  className="text-xl mb-8 text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  Book your theatrical dining experience today and create memories that last a lifetime!
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Link
                    to="/branches"
                    className="inline-flex items-center px-8 py-3 rounded-full font-medium"
                    style={{
                      backgroundColor: 'white',
                      color: 'var(--primary)',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    Book a Slot
                  </Link>
                </motion.div>
              </div>
            </div>
          </section>

          <ServicesSlider />
        </div>
      </div>
    </>
  );
} 